<template>
  <div class="customer__form modal modal--default">
    <div class="modal__panel modal__panel--termination">
      <div class="modal__logo-wrapper">
        <div class="modal__logo"></div>
      </div>

      <div class="modal__main-icon modal__main-icon--notes"></div>
      <div class="modal__back" @click="goBack()"></div>
      <div class="modal__close" @click="closeModal()"></div>

      <div class="form form--terminate">
        <div class="navigators">
          <div class="navigator navigator--active">
            <div class="navigator__bullet"></div>
            <div class="navigator__title">
              {{ $t(`addNoteModal.${"title"}`) }}
            </div>
          </div>
        </div>
        <div class="add__form add__form--terminate">
          <transition name="fade" mode="out-in">
            <Loading v-if="formLoading" />
            <div v-else class="add__content">
              <div class="termination">
                <div class="termination__row">
                  <div class="termination__column">
                    <div class="form__field">
                       
                      <div class="input input--default input--readonly">
                        {{ parseCustomerName(customerDetails) }}
                      </div>
                    </div>
                    <div class="form__field">
                   
                      <Datepicker class="input input--default" :config="statics.datePickerConfig" :placeholder="$t(`addNoteModal.${'dateDropdown'}`)" v-model="deadline" />
                    </div>
                    <div class="form__field"> 
                      <input class="input input--default input" v-model="title" :placeholder="$t(`addNoteModal.${'subjectTitle'}`)">
                    </div>
                  </div>
                  <div class="termination__column termination__column--middle">
                    <div class="form__field"> 
                      <VSelect class="select select--default" :options="priorities"
                        :class="{ 'select--error': priorityHasError }" :reduce="(priority) => priority.id" label="type"
                        :placeholder="$t(`addNoteModal.${'priorityDropdown'}`)" v-model="priorityId" />
                      <div class="form__error" v-if="priorityHasError">
                        {{ $t(`addNoteModal.${"priorityErrorMsg"}`) }}
                      </div>
                    </div>

                    <div class="form__field"> 
                      <VSelect class="select select--default" :class="{ 'select--error': agentHasError }"
                        :options="agents" :reduce="(agent) => agent.id" :getOptionLabel="(agent) => `${agent.first_name} ${agent.last_name}`" :placeholder="$t(`addNoteModal.${'beraterDropdown'}`)" v-model="agentId" />
                      <div class="form__error" v-if="agentHasError">
                        {{ $t(`addNoteModal.${"agentErrorMsg"}`) }}
                      </div>
                    </div>
                  </div>
                  <div class="termination__column"> 
                    <DropFile @input="files = $event" />
                  </div>
                </div>
                <div class="termination__row termination__textarea__row">
                  <div class="form__field form__notes__textarea"> 
                    <textarea :placeholder="$t(`addNoteModal.${'text'}`)" cols="30" rows="10"
                      class="input input--default termination__textarea" v-model="description"
                      style="max-width: initial !important;"></textarea>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
      <div v-if="isRequesting" class="modal__button modal__button--submit button button--default button--loading"></div>

      <div v-else>
        <button v-if="!isNoteEditing" class="modal__button modal__button--submit button button--default"
          @click="addNotes()">
          <span>{{ $t(`addNoteModal.${'saveBtn'}`) }}</span>
        </button>
        <button v-else class="modal__button modal__button--submit button button--default" @click="editNotes()">
          <span>{{ $t(`addNoteModal.${'editBtn'}`) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, getCurrentInstance } from "vue";
import { useStore } from "vuex";
import DropFile from "@/components/DropFile";
import Loading from "@/partials/Loading";
import { parseCustomerName } from "@/helpers/entityParsers";
import statics from "@/statics";
import { useI18n } from 'vue-i18n';   

// eslint-disable-next-line no-unused-vars
const { t } = useI18n();
defineOptions({
  name: "AddPotentialPolicyModal" // Left intact as per the original script name option
});

const store = useStore();

// Access global proxy for $checkRolePermission method mapping
const { proxy } = getCurrentInstance();

// Reactive properties
const formLoading = ref(true);
const isRequesting = ref(false);

const deadline = ref("");
const priorityId = ref("");
const title = ref("");
const agentId = ref("");
const description = ref("");
const teamId = ref(null);

const files = ref([]);

const agentHasError = ref(false);
const priorityHasError = ref(false);

// Watchers
watch(agentId, (value) => {
  agentHasError.value = !value;
});

watch(priorityId, (value) => {
  priorityHasError.value = !value;
});

// Computed Properties
const customerDetails = computed(() => store.getters.customerDetails);
const notesForms = computed(() => store.getters.notesForms);
const isNoteEditing = computed(() => store.getters.isNoteEditing);
const editingNoteData = computed(() => store.getters.editingNoteData);

const agents = computed(() => notesForms.value.agents);
const priorities = computed(() => notesForms.value.priorities);

// Methods
const closeModal = () => {
  store.commit("setShowAddNotesModal", false);
  store.commit("setIsNoteEditing", false);
  store.commit("setEditingNoteData", null);
};

const goBack = () => {
  closeModal();
  store.commit("setShowAddActivityModal", true);
};

const validateFields = (callback) => {
  agentHasError.value = !agentId.value;
  priorityHasError.value = !priorityId.value;

  if (!agentHasError.value && !priorityHasError.value) {
    callback(true);
  } else {
    callback(false);
  }
};

const addNotes = async () => {
  isRequesting.value = true;
  validateFields(async (isValid) => {
    if (isValid) {
      try {
        const noteId = await store.dispatch('createNotes', {
          agent_id: agentId.value,
          customer_id: customerDetails.value.id,
          priority_id: priorityId.value,
          title: title.value,
          deadline: deadline.value,
          description: description.value,
        });

        closeModal();

        if (files.value.length > 0) {
          const mappedFiles = files.value.map((file) => file.file);
          await store.dispatch("addNotesFile", {
            noteId,
            files: mappedFiles,
          });
        }
      } catch (error) {
        // Error already handled by store action
      } finally {
        isRequesting.value = false;
      }
    } else {
      store.commit("setToast", {
        display: true,
        type: "error",
        message: t("messages.validationErrorMsg"),
      });
      isRequesting.value = false;
    }
  });
};

const editNotes = async () => {
  isRequesting.value = true;
  validateFields(async (isValid) => {
    if (isValid) {
      try {
        await store.dispatch('updateNotes', {
          id: editingNoteData.value.id,
          agent_id: agentId.value,
          customer_id: customerDetails.value.id,
          priority_id: priorityId.value,
          title: title.value,
          deadline: deadline.value,
          description: description.value,
        });

        closeModal();
      } catch (error) {
        // Error already handled by store action
      } finally {
        isRequesting.value = false;
      }
    } else {
      store.commit("setToast", {
        display: true,
        type: "error",
        message: t("messages.validationErrorMsg"),
      });
      isRequesting.value = false;
    }
  });
};

// Lifecycle initialization (replaces created hook)
onMounted(async () => {
  formLoading.value = true;

  if (!(proxy.$checkRolePermission('note_create') || proxy.$checkRolePermission('note_edit'))) {
    closeModal();
  } else {
    if (customerDetails.value.team) {
      teamId.value = Number(customerDetails.value.team.id);
      await store.dispatch("getNotesForms", teamId.value);
    }

    if (isNoteEditing.value) {
      agentId.value = editingNoteData.value.agent.id;
      priorityId.value = editingNoteData.value.priority.id;
      title.value = editingNoteData.value.title;
      deadline.value = editingNoteData.value.deadline;
      description.value = editingNoteData.value.description;
    }
  }

  formLoading.value = false;
});

onUnmounted(() => {
  closeModal();
});
</script>

<style scoped lang="scss">
@use "../assets/scss/termination-potential-policy";

.termination__row {
  border: 0;
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
}

.termination__row:last-of-type {
  margin-bottom: 0;
}

.termination__column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.termination__column--middle {
  flex: 1;
}

.termination__textarea__row {
  margin-top: 8px;
}

.form__notes__textarea {
  width: 100%;
  flex: 1;
}

.form__field {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

.form__field:last-child {
  margin-bottom: 0;
}

.form__error {
  margin-top: 4px;
}

.date-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--text-3);
}

.label-block {
  font-weight: 500;
  text-transform: capitalize;
}

.termination__textarea {
  resize: vertical;
  min-height: 120px;
}

.input--readonly {
  background-color: var(--bg-3);
  cursor: not-allowed;
  color: var(--text-2);
  padding: 10px 12px;
  border-radius: var(--radius-1);
  border: 1px solid transparent;
}
</style>
